// @flow

import { useCallback, useEffect, useMemo, useRef } from 'react';

import useModalProps from '../../hooks/useModalProps';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import {
  useDeleteTourBlockout,
  useTourBlockoutsQuery,
} from '../../hooks/data-fetching/useTourBlockouts';
import appMessages from '../App/messages';
import messages from './messages';
import { formatInZone, readTourBlockoutError } from './utils';
import type { TourBlockout, TourBlockoutsViewModel } from './types';

const TABLE_NAME = 'tour-blockouts';

const useTourBlockouts = ({
  intl,
  promptToaster,
}: {
  intl: Object,
  promptToaster: (payload: Object) => void,
}): TourBlockoutsViewModel => {
  const selectedProperty = useSelectedProperty();
  const organizationId: ?string = selectedProperty?.organizationId;
  const propertyId: ?string = selectedProperty?.id;
  const timezone: ?string = selectedProperty?.config?.timezone ?? null;
  const tableName = useUniqueTableName(TABLE_NAME);
  const modal = useModalProps();
  const {
    handleOpen,
    handleClose,
    handleOpenConfirmModal,
    handleCloseConfirmModal,
  } = modal;

  // An open form or delete confirmation belongs to one property. If the
  // property changes under it (the browser's back button, say), it closes
  // rather than save or delete at the new property.
  const shownProperty = useRef(propertyId);
  useEffect(() => {
    if (shownProperty.current === propertyId) return;
    shownProperty.current = propertyId;
    handleClose();
    handleCloseConfirmModal();
  }, [propertyId, handleClose, handleCloseConfirmModal]);

  const query = useTourBlockoutsQuery({ organizationId, propertyId });
  const deleteBlockout = useDeleteTourBlockout({
    organizationId: organizationId ?? '',
    propertyId: propertyId ?? '',
  });

  const headers = useMemo(
    () => [
      { id: 'startTime', label: intl.formatMessage(messages.start) },
      { id: 'endTime', label: intl.formatMessage(messages.end) },
      { id: 'label', label: intl.formatMessage(messages.label) },
      { id: 'slotsBlocked', label: intl.formatMessage(messages.slotsBlocked) },
      { id: 'actions', label: intl.formatMessage(appMessages.actions) },
    ],
    [intl],
  );

  const blockouts: Array<TourBlockout> = useMemo(
    () => query.data ?? [],
    [query.data],
  );

  const rows = useMemo(
    () =>
      blockouts.map((tourBlockout) => ({
        id: tourBlockout.id,
        // Shown in the property's zone, as the form enters them.
        startTime: {
          value: formatInZone(tourBlockout.startTime, timezone, intl.locale),
        },
        endTime: {
          value: formatInZone(tourBlockout.endTime, timezone, intl.locale),
        },
        label: { value: tourBlockout.label },
        slotsBlocked: { variant: 'number', value: tourBlockout.slotsBlocked },
        actions: {
          variant: 'menu',
          name: 'actionsMenu',
          iconName: 'BoltIcon',
          color: 'primary',
          closeOnClick: true,
          options: [
            {
              value: 'edit',
              text: intl.formatMessage(appMessages.edit),
              onClick: () => handleOpen({ modalType: 'edit', tourBlockout }),
            },
            {
              value: 'delete',
              text: intl.formatMessage(messages.delete),
              onClick: () =>
                handleOpenConfirmModal({ modalType: 'delete', tourBlockout }),
            },
          ],
        },
      })),
    [blockouts, timezone, intl, handleOpen, handleOpenConfirmModal],
  );

  const handleAdd = useCallback(
    () => handleOpen({ modalType: 'add' }),
    [handleOpen],
  );

  const blockoutToDelete: ?TourBlockout =
    modal.modalType === 'delete' ? modal.modalState.tourBlockout : null;

  // The confirmation as last rendered. Escape or a property change can close
  // it while a delete runs, and the dialogs share their state, so a delete that
  // finishes later may only close the confirmation still showing its blockout.
  const shownConfirmation = useRef({});
  shownConfirmation.current = {
    open: modal.openConfirm,
    id: blockoutToDelete?.id,
  };

  const handleDelete = useCallback(() => {
    if (!blockoutToDelete) {
      return;
    }
    const deletedId = blockoutToDelete.id;
    deleteBlockout.mutate(deletedId, {
      onSuccess: () => {
        promptToaster({
          title: intl.formatMessage(appMessages.success),
          message: intl.formatMessage(messages.deleted),
        });
        const { open, id } = shownConfirmation.current;
        if (open && id === deletedId) handleCloseConfirmModal();
      },
      onError: (error) => {
        promptToaster({
          type: 'error',
          title: intl.formatMessage(appMessages.error),
          message: readTourBlockoutError(
            error,
            intl.formatMessage(messages.deleteError),
          ).message,
        });
      },
    });
  }, [
    blockoutToDelete,
    deleteBlockout,
    promptToaster,
    intl,
    handleCloseConfirmModal,
  ]);

  // A failed load always says so, with the backend's reason when it gave one;
  // it never falls through to the empty list.
  const loadError = query.isError
    ? readTourBlockoutError(query.error, intl.formatMessage(messages.loadError))
        .message
    : null;

  return {
    title: intl.formatMessage(messages.title),
    timezone,
    rows,
    headers,
    tableName,
    // A query waiting for the property (react-query v3 "idle") is still loading.
    isLoading: query.isLoading || query.isIdle,
    loadError,
    emptyStateMessage: intl.formatMessage(messages.emptyState),
    handleAdd,
    modalProps: {
      open: modal.open,
      tourBlockout:
        modal.modalType === 'edit' ? modal.modalState.tourBlockout : null,
      handleClose: modal.handleClose,
    },
    deleteConfirmProps: {
      open: Boolean(modal.openConfirm && blockoutToDelete),
      title: intl.formatMessage(messages.deleteTitle),
      children: intl.formatMessage(messages.deleteBody, {
        label: blockoutToDelete?.label ?? '',
      }),
      onClose: handleCloseConfirmModal,
      actionsProps: [
        {
          children: intl.formatMessage(appMessages.cancel),
          onClick: handleCloseConfirmModal,
          disabled: deleteBlockout.isLoading,
        },
        {
          children: intl.formatMessage(messages.delete),
          onClick: handleDelete,
          submitButton: true,
          isSubmitting: deleteBlockout.isLoading,
          disabled: deleteBlockout.isLoading,
        },
      ],
    },
  };
};

export default useTourBlockouts;
